import { Alert, Box, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { formatFilename, formMaxWidth } from '../constants/general-info-constants';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProjectStakeholders,
  postProjectStakeholders
} from '../../../../../../../actions/rof-burns/01-project-info/01-general-info';
import GeneralInfoForm from '../shared-components/GeneralInfoForm';
import tooltips from '../../../../../../../store/tooltips';

const defaultFormState = {
  stakeholderDetails: '',
  stakeholderName: '',
  stakeholderRole: '',
  stakeholderCompany: '',
  stakeholderContact: '',
  proofOfInvolvement: null,
}

export default function ProjectStakeholders({ projectId, selectedCategory }) {
  const dispatch = useDispatch()
  const [formState, setFormState] = useState(defaultFormState)
  const [formErrors, setFormErrors] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)

  const projectStakeholdersData = useSelector((state) => (state.generalInfoReducer.projectStakeholders?.data));

  useEffect(() => {
    handleFormErrorReset()
    dispatch(getProjectStakeholders(projectId))
  }, [selectedCategory, projectId])

  const projectStakeholderQuestions = (formState) => {
    const { stakeholderDetails, stakeholderName, stakeholderRole,
      stakeholderCompany, stakeholderContact } = formState

    return [
      {
        type: 'text',
        fieldId: 'stakeholderDetails',
        text: 'Please list any other stakeholders involved in the project during planning, design, implementation or post-implementation:',
        value: stakeholderDetails,
        placeholder: 'May include Funders, Project Developers, Office Administrators, Forestry Contractors, Laboratories, Soil Specialists, etc.',
        rows: 3,
        multiline: true,
        tooltip: tooltips.proofProjectQ1
      },
      {
        type: 'text',
        fieldId: 'stakeholderName',
        text: 'Stakeholder name:',
        value: stakeholderName,
        placeholder: 'Stakeholder name details',
      },
      {
        type: 'text',
        fieldId: 'stakeholderRole',
        text: 'Stakeholder role:',
        value: stakeholderRole,
        placeholder: 'Stakeholder role details'
      },
      {
        type: 'text',
        fieldId: 'stakeholderCompany',
        text: 'Stakeholder company:',
        value: stakeholderCompany,
        placeholder: 'Stakeholder company details'
      },
      {
        type: 'text',
        fieldId: 'stakeholderContact',
        text: 'Stakeholder contact e-mail:',
        value: stakeholderContact,
        placeholder: 'Stakeholder contact e-mail'
      },
      {
        type: 'file',
        fieldId: 'proofOfInvolvement',
        text: 'Please provide proof of their involvement (Contract or other documentation)',
      }
    ]
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)
      validateProjectStakeholders(formState)

      const formData = createFormData(formState)

      await dispatch(postProjectStakeholders(formData, projectId))
      await dispatch(getProjectStakeholders(projectId))
      resetForm()
    } catch (error) {
      setFormErrors(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  function createFormData(formState) {
    const formData = new FormData()

    formData.append('stakeholderDetails', formState.stakeholderDetails)
    formData.append('stakeholderName', formState.stakeholderName)
    formData.append('stakeholderRole', formState.stakeholderRole)
    formData.append('stakeholderCompany', formState.stakeholderCompany)
    formData.append('stakeholderContact', formState.stakeholderContact)

    if (formState.proofOfInvolvement instanceof File) {
      formData.append('proofOfInvolvement', formState.proofOfInvolvement)
    }
    return formData
  }

  const setData = (event) => {
    const { name, type } = event.target;
    let value = event.target.value

    if (type === 'file') {
      value = event.target.files[0]
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }

  function validateProjectStakeholders(form) {
    for (let key in form) {
      if (form[key] === '') {
        throw Error('Please complete all form fields before submitting.')
      }
      else if (form[key] === null) {
        throw Error('Please upload a proof of involvement before submitting.')
      }
    }
  }

  const handleFormErrorReset = () => {
    setFormErrors(null);
  };

  function resetForm() {
    setFileInputKey((prev) => (prev + 1))
    setFormState(defaultFormState)
  }

  const questions = projectStakeholderQuestions(formState)

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: formMaxWidth }}>
        <Typography variant='h4' sx={{ pb: 2 }}>
          {selectedCategory}
        </Typography>
        <GeneralInfoForm
          formObjectArray={questions}
          formState={formState}
          formErrors={formErrors}
          isLoading={isLoading}
          fileInputKey={fileInputKey}
          onChange={setData}
          onSubmit={onSubmit} />
      </Box>
      <ProjectStakeholdersTable projectStakeholdersData={projectStakeholdersData} />
    </>
  )
}

const projectStakeholdersTableColumns = [
  'Stakeholder Name',
  'Stakeholder Role',
  'Stakeholder Company',
  'Stakeholder Contact',
  'Stakeholder Details',
  'Proof of Involvement',
]

export function ProjectStakeholdersTable({ projectStakeholdersData }) {
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='h5' sx={{ py: 3 }}>List of Project Stakeholders</Typography>
        {
          <TableContainer component={Paper}>
            <Table aria-label='simple table'>
              <TableHead>
                <TableRow>
                  {projectStakeholdersTableColumns.map((column, index) => (
                    <TableCell sx={{ fontSize: 20, fontWeight: 'bold' }} align='left' key={index}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  projectStakeholdersData?.length > 0 && projectStakeholdersData?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}>
                      <TableCell align='left'>{row.stakeholder_name}</TableCell>
                      <TableCell align='left'>{row.stakeholder_role}</TableCell>
                      <TableCell align='left'>{row.stakeholder_company}</TableCell>
                      <TableCell align='left'>{row.stakeholder_contact}</TableCell>
                      <TableCell align='left'>{row.stakeholder_details}</TableCell>
                      <TableCell align='left'>
                        <Link href={row.proof_of_involvement.fileurl} target='_blank'>
                          {formatFilename(row.proof_of_involvement.filename)}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
        }
        {
          projectStakeholdersData?.length === 0 &&
          <NoProjectDescriptionData />
        }
      </Box>
    </>
  )
}

function NoProjectDescriptionData() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
      <Alert severity='info' sx={{ width: 'fit-content' }}>No Project Stakeholder data uploaded yet. Submitting details will populate this table.</Alert>
    </Box>
  )
}